import { Tag, Typography, Button, Space, Tooltip } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import _get from "lodash/get";

const { Text } = Typography;

const getProcessingStatusTag = (status) => {
  switch (status) {
    case "completed":
      return <Tag color="green">Hoàn thành</Tag>;
    case "processing":
      return <Tag color="blue">Đang gửi</Tag>;
    case "pending":
      return <Tag color="orange">Chờ gửi</Tag>;
    default:
      return <Tag>{status || "N/A"}</Tag>;
  }
};

const getRetryStatusTag = (retryStatus, retryCount) => {
  if (!retryStatus || retryStatus === "none") {
    return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
  }
  const countText = retryCount > 0 ? ` (${retryCount})` : "";
  switch (retryStatus) {
    case "completed":
      return <Tag color="green">Xong{countText}</Tag>;
    case "processing":
      return <Tag color="blue">Đang retry{countText}</Tag>;
    case "pending":
      return <Tag color="orange">Chờ retry{countText}</Tag>;
    default:
      return <Tag>{retryStatus}{countText}</Tag>;
  }
};

const getSuccessRateColor = (rate) => {
  if (rate >= 90) return "green";
  if (rate >= 70) return "orange";
  return "red";
};

const SystemNotificationColumn = ({ onRefresh, pagination, onRetry }) => {
  const navigate = useNavigate();

  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "");
  };

  const canRetry = (record) => {
    return (
      record.failedDevices > 0 &&
      record.retryStatus !== "processing" &&
      record.processingStatus === "completed"
    );
  };

  return [
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
      width: 200,
      render: (text, record) => (
        <div style={{ fontWeight: 500, color: "#333" }}>
          {text || record.metaData?.title || "N/A"}
        </div>
      ),
    },
    {
      title: "Nội dung",
      dataIndex: "message",
      key: "message",
      width: 250,
      render: (text) => (
        <div style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          maxWidth: 250,
        }}>
          {stripHtml(text) || "N/A"}
        </div>
      ),
    },
    {
      title: "Người nhận",
      dataIndex: "affectedUsers",
      key: "affectedUsers",
      width: 100,
      render: (affectedUsers) => (
        <Tag color="orange" style={{ fontWeight: 500 }}>
          {affectedUsers || 0}
        </Tag>
      ),
    },
    {
      title: "Thiết bị",
      dataIndex: "numberOfDevices",
      key: "numberOfDevices",
      width: 90,
      render: (numberOfDevices) => (
        <Tag color="blue" style={{ fontWeight: 500 }}>
          {numberOfDevices || 0}
        </Tag>
      ),
    },
    {
      title: "Thành công",
      dataIndex: "successDevices",
      key: "successDevices",
      width: 100,
      render: (val) => (
        <Tag color="green" style={{ fontWeight: 500 }}>
          {val || 0}
        </Tag>
      ),
    },
    {
      title: "Thất bại",
      dataIndex: "failedDevices",
      key: "failedDevices",
      width: 90,
      render: (val) =>
        val > 0 ? (
          <Tag color="red" style={{ fontWeight: 500 }}>
            {val}
          </Tag>
        ) : (
          <Tag style={{ fontWeight: 500 }}>0</Tag>
        ),
    },
    {
      title: "Tỷ lệ",
      dataIndex: "successRate",
      key: "successRate",
      width: 80,
      render: (val) =>
        val != null ? (
          <Tag color={getSuccessRateColor(val)} style={{ fontWeight: 500 }}>
            {val}%
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
        ),
    },
    {
      title: "Trạng thái",
      dataIndex: "processingStatus",
      key: "processingStatus",
      width: 110,
      render: (status) => getProcessingStatusTag(status),
    },
    {
      title: "Retry",
      key: "retryInfo",
      width: 110,
      render: (_, record) => getRetryStatusTag(record.retryStatus, record.retryCount),
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (date) => (
        <Text style={{ fontSize: 12, color: "#666" }}>
          {date ? moment(date).format("DD/MM/YYYY HH:mm") : "N/A"}
        </Text>
      ),
    },
    {
      title: "Hành động",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/system_notifications/${record._id}`)}
              style={{ color: "#1890ff" }}
            />
          </Tooltip>
          {canRetry(record) && onRetry && (
            <Tooltip title="Gửi lại cho thiết bị thất bại">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => onRetry(record._id)}
                style={{ color: "#ff4d4f" }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];
};

export default SystemNotificationColumn;
